import { apiSlice } from './apiSlice';
import { ApiResponse } from '@/types';
import {
  handleOptimisticDelete,
  handleOptimisticUpdate,
  handleOptimisticCreate,
} from './optimisticUpdates';

export type EventStatus = 'Draft' | 'Published';

export interface Event {
  id: number;
  title: string;
  description: string;
  eventDate: string;
  registrationLink: string | null;
  imageUrl: string | null;
  venue: string | null;
  status: EventStatus;
  seats: number | null;
  acceptingJoins: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { joinRequests: number };
}

export interface PaginatedEventsResponse {
  count: number;
  skip: number;
  take: number;
  data: Event[];
}

export interface GetEventsArgs {
  search?: string;
  skip?: number;
  take?: number;
  status?: EventStatus;
  timeFilter?: 'Upcoming' | 'Past' | '' | 'Month' | 'Day';
}

export type CreateEventPayload = Omit<Event, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateEventPayload = Partial<CreateEventPayload>;

export interface EventJoinRequest {
  id: number;
  eventId: number;
  fullName: string;
  email: string;
  phoneNumber?: string;
  linkedin?: string;
  ticketSent: boolean;
  hasAttended: boolean;
  attendedAt?: string;
  createdAt: string;
  event?: Event;
}

export interface PaginatedJoinRequestsResponse {
  count: number;
  skip: number;
  take: number;
  data: EventJoinRequest[];
}

export const eventsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<PaginatedEventsResponse, GetEventsArgs | void>({
      query: (args) => {
        let url = '/events';
        const params = new URLSearchParams();
        if (args) {
          if (args.search) params.append('search', args.search);
          if (args.skip !== undefined) params.append('skip', String(args.skip));
          if (args.take !== undefined) params.append('take', String(args.take));
          if (args.status) params.append('status', args.status);
          if (args.timeFilter) params.append('timeFilter', args.timeFilter);
        }
        const qs = params.toString();
        if (qs) url += `?${qs}`;
        return url;
      },
      transformResponse: (response: ApiResponse<PaginatedEventsResponse>) => response.data,
      providesTags: ['Event'],
    }),

    getEventById: builder.query<Event, string>({
      query: (slug) => `/events/${slug}`,
      transformResponse: (response: ApiResponse<Event>) => response.data,
      providesTags: (_result, _err, slug) => [{ type: 'Event', id: slug }],
    }),

    createEvent: builder.mutation<Event, CreateEventPayload>({
      query: (body) => ({ url: '/events', method: 'POST', body }),
      transformResponse: (response: ApiResponse<Event>) => response.data,
      invalidatesTags: ['Event'],
    }),

    updateEvent: builder.mutation<Event, { id: number; body: UpdateEventPayload }>({
      query: ({ id, body }) => ({ url: `/events/${id}`, method: 'PATCH', body }),
      transformResponse: (response: ApiResponse<Event>) => response.data,
      invalidatesTags: ['Event'],
    }),

    deleteEvent: builder.mutation<void, number>({
      query: (id) => ({ url: `/events/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Event'],
    }),

    getEventJoinRequests: builder.query<
      PaginatedJoinRequestsResponse,
      {
        skip?: number;
        take?: number;
        search?: string;
        eventId?: string;
        timeFilter?: 'Upcoming' | 'Past' | '';
      } | void
    >({
      query: (args) => {
        let url = '/events/join-requests/all';
        const params = new URLSearchParams();
        if (args) {
          if (args.skip !== undefined) params.append('skip', String(args.skip));
          if (args.take !== undefined) params.append('take', String(args.take));
          if (args.search) params.append('search', args.search);
          if (args.eventId) params.append('eventId', args.eventId);
          if (args.timeFilter) params.append('timeFilter', args.timeFilter);
        }
        const qs = params.toString();
        if (qs) url += `?${qs}`;
        return url;
      },
      transformResponse: (response: ApiResponse<PaginatedJoinRequestsResponse>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Event' as const, id: `JOIN_REQ_${id}` })),
              { type: 'Event', id: 'JOIN_REQUEST_LIST' },
            ]
          : [{ type: 'Event', id: 'JOIN_REQUEST_LIST' }],
    }),

    deleteEventJoinRequest: builder.mutation<void, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/events/join-requests/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: `JOIN_REQ_${arg.id}` },
        { type: 'Event', id: 'JOIN_REQUEST_LIST' },
      ],
    }),

    toggleEventJoins: builder.mutation<Event, { id: number; acceptingJoins: boolean }>({
      query: ({ id, acceptingJoins }) => ({
        url: `/events/${id}`,
        method: 'PATCH',
        body: { acceptingJoins },
      }),
      transformResponse: (response: ApiResponse<Event>) => response.data,
      invalidatesTags: ['Event'],
    }),

    resendEventTicket: builder.mutation<void, { eventId: number; requestId: number }>({
      query: ({ eventId, requestId }) => ({
        url: `/events/${eventId}/join-requests/${requestId}/resend-ticket`,
        method: 'POST',
      }),
      invalidatesTags: ['Event'],
    }),

    getTicketDetails: builder.query<EventJoinRequest, string>({
      query: (ticketId) => `/events/ticket-details?ticketId=${ticketId}`,
      transformResponse: (response: ApiResponse<EventJoinRequest>) => response.data,
      providesTags: ['Event'],
    }),

    verifyTicket: builder.mutation<EventJoinRequest, string>({
      query: (ticketId) => ({
        url: `/events/verify-ticket`,
        method: 'POST',
        body: { ticketId },
      }),
      transformResponse: (response: ApiResponse<EventJoinRequest>) => response.data,
      invalidatesTags: ['Event'],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetEventJoinRequestsQuery,
  useLazyGetEventJoinRequestsQuery,
  useDeleteEventJoinRequestMutation,
  useToggleEventJoinsMutation,
  useResendEventTicketMutation,
  useGetTicketDetailsQuery,
  useVerifyTicketMutation,
} = eventsApi;
